import React, { FC, useContext, useEffect, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { Form, Button } from "@natera/form";
import { ErrorContext, IntlContext } from "@app/provider";
import { PhoneField } from "@app/components/updatePhone";
import { formatDateToISO } from "@app/utils/convertDate";
import {
  getMaxDayForDob,
  getMonthsMap,
  MonthsMapOption,
} from "@app/components/dob/months";
import { getLowerYearLimit, isPatientMinor } from "@app/utils";
import DobMonthField from "@app/components/dob/DobMonthField";
import DobYearField from "@app/components/dob/DobYearField";
import DobDayField from "@app/components/dob/DobDayField";
import { useDobValidation } from "@app/hooks/useDobValidation";

import "./dobVerificationForm.scss";

const messages = defineMessages({
  dateOfBirthVerificationVerify: {
    id: "dateOfBirthVerificationVerify",
    defaultMessage: "Verify",
  },
  dateOfBirthVerificationRequired: {
    id: "dateOfBirthVerificationRequired",
    defaultMessage: "This information is required",
  },
  dateOfBirthVerificationInvalidValue: {
    id: "dateOfBirthVerificationInvalidValue",
    defaultMessage: "Invalid or incomplete value.",
  },
  dateOfBirthVerificationInvalidDob: {
    id: "dateOfBirthVerificationInvalidDob",
    defaultMessage: "Incorrect date of birth. Please re-enter",
  },
  dateOfBirthVerificationInvalidPhoneNumber: {
    id: "scheduleBloodDrawDobInvalidPhoneNumber",
    defaultMessage: "Please enter a valid phone number",
  },
  dateOfBirthVerificationUnder18DateOfBirth: {
    id: "dateOfBirthVerificationUnder18DateOfBirth",
    defaultMessage:
      "The patient portal does not currently have test information for patients under 18 years old. Please reach out to your provider, or contact support@natera.com, for information on tests for patients under 18.",
  },
});

export type DateOfBirthSubmitProps = {
  dateOfBirth: string;
  phoneNumber?: string;
};

interface DateOfBirthProps {
  loading: boolean;
  onSubmit: (props: DateOfBirthSubmitProps) => void;
  isPhoneRequired: boolean;
}

export enum DOB_VALIDATION_ERROR {
  INVALID_DAY = "INVALID_DAY",
  INCORRECT_DOB = "INCORRECT_DOB",
  INCORRECT_YEAR = "INCORRECT_YEAR",
  INCORRECT_MOBILE = "INCORRECT_MOBILE",
  REQUIRED = "REQUIRED",
  UNDER_18 = "UNDER_18",
}

const DobVerificationForm: FC<DateOfBirthProps> = ({
  loading,
  onSubmit,
  isPhoneRequired,
}) => {
  const currentYear = new Date().getFullYear();
  const lowerYearLimit = getLowerYearLimit();

  const { currentLanguage } = useContext(IntlContext);

  const intl = useIntl();
  const {
    getValidationError,
    setValidationError,
    clearValidationError,
    getError,
    clearErrors,
  } = React.useContext(ErrorContext);

  const error = getError();

  const [year, setYear] = useState<string>("");
  const [selectedMonth, setSelectedMonth] = useState<MonthsMapOption | null>(
    null
  );
  const [selectedDay, setSelectedDay] = useState<number | undefined>(undefined);
  const [dayInputValue, setDayInputValue] = useState<string>("");

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Block only on client-side format errors; do not block on dateOfBirthIncorrect
    // (backend "wrong DOB" result) so repeated Verify clicks count as attempts.
    if (
      getValidationError("dateOfBirthDay") ||
      getValidationError("dateOfBirthYear") ||
      getValidationError("dateOfBirthMonth")
    ) {
      return;
    }

    const formData = new FormData(event.currentTarget);

    const month = selectedMonth
      ? selectedMonth.value.toString().padStart(2, "0")
      : undefined;
    const day = formData.get("dateOfBirthDay")?.toString();
    const year = formData.get("dateOfBirthYear")?.toString();
    const phone = formData.get("mobileNumber")?.toString();

    if (!month || !day || !year) {
      renderErrorMessage(DOB_VALIDATION_ERROR.REQUIRED);
      return;
    }

    if (year.length !== 4) {
      renderErrorMessage(DOB_VALIDATION_ERROR.INCORRECT_YEAR);
      return;
    }

    const maxDay = getMaxDayForDob(selectedMonth!.value, parseInt(year, 10));

    if (selectedDay && selectedDay > maxDay) {
      renderErrorMessage(DOB_VALIDATION_ERROR.INVALID_DAY);
      return;
    }

    if (isPhoneRequired && !phone) {
      setValidationError(
        "mobileNumber",
        intl.formatMessage(messages.dateOfBirthVerificationInvalidPhoneNumber)
      );
      return;
    }

    const dateOfBirth = formatDateToISO({
      year,
      month,
      day,
    });

    const dOB = new Date(dateOfBirth);

    if (isPatientMinor(dOB)) {
      renderErrorMessage(DOB_VALIDATION_ERROR.UNDER_18);
      return;
    }

    clearErrors();
    onSubmit({ dateOfBirth, phoneNumber: phone });
  };

  const renderErrorMessage = (error?: string) => {
    if (!error) return;

    switch (error) {
      case DOB_VALIDATION_ERROR.INVALID_DAY:
        setValidationError(
          "dateOfBirthDay",
          intl.formatMessage(messages.dateOfBirthVerificationInvalidValue)
        );
        break;

      case DOB_VALIDATION_ERROR.INCORRECT_DOB:
        setValidationError(
          "dateOfBirthIncorrect",
          intl.formatMessage(messages.dateOfBirthVerificationInvalidDob)
        );
        break;

      case DOB_VALIDATION_ERROR.INCORRECT_MOBILE:
        setValidationError(
          "mobileNumber",
          intl.formatMessage(messages.dateOfBirthVerificationInvalidPhoneNumber)
        );
        break;

      case DOB_VALIDATION_ERROR.REQUIRED:
        setValidationError(
          "dateOfBirthIncorrect",
          intl.formatMessage(messages.dateOfBirthVerificationRequired)
        );
        break;

      case DOB_VALIDATION_ERROR.INCORRECT_YEAR:
        setValidationError(
          "dateOfBirthYear",
          intl.formatMessage(messages.dateOfBirthVerificationInvalidDob)
        );
        break;

      case DOB_VALIDATION_ERROR.UNDER_18:
        setValidationError(
          "dateOfBirthYear",
          intl.formatMessage(messages.dateOfBirthVerificationUnder18DateOfBirth)
        );
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    if (error) {
      renderErrorMessage(error as unknown as DOB_VALIDATION_ERROR);
    }
  }, [error, currentLanguage]);

  useDobValidation(selectedMonth, selectedDay, year, {
    setValidationError,
    clearValidationError,
    invalidDayMessage: intl.formatMessage(
      messages.dateOfBirthVerificationInvalidValue
    ),
    under18Message: intl.formatMessage(
      messages.dateOfBirthVerificationUnder18DateOfBirth
    ),
    validateAge: true,
    lowerYearLimit,
    currentYear,
  });

  return (
    <div className="dob-verification-form">
      <section className="dob-verification-form__container">
        <Form noValidate onSubmit={handleSubmit}>
          <div className="dob-verification-form__row">
            <DobMonthField
              monthsMap={getMonthsMap(intl)}
              selectedMonth={selectedMonth}
              selectedDay={selectedDay}
              selectedYear={year}
              setSelectedMonth={setSelectedMonth}
              getValidationError={getValidationError}
              setValidationError={setValidationError}
              clearValidationError={clearValidationError}
            />
            <DobDayField
              setDayInputValue={setDayInputValue}
              dayInputValue={dayInputValue}
              selectedMonth={selectedMonth}
              selectedYear={year}
              setSelectedDay={setSelectedDay}
              getValidationError={getValidationError}
              setValidationError={setValidationError}
              clearValidationError={clearValidationError}
            />
          </div>
          <div className="dob-verification-form__year">
            <DobYearField
              setYear={setYear}
              year={year}
              lowerYearLimit={lowerYearLimit}
              currentYear={currentYear}
              getValidationError={getValidationError}
              setValidationError={setValidationError}
              clearValidationError={clearValidationError}
            />
          </div>
          {isPhoneRequired && (
            <div className="dob-verification-form__phone">
              <PhoneField getValidationError={getValidationError} />
            </div>
          )}

          <Button
            type="submit"
            unelevated
            loading={loading}
            className="dob-verification-form__submit"
          >
            {intl.formatMessage(messages.dateOfBirthVerificationVerify)}
          </Button>
        </Form>
      </section>
    </div>
  );
};

export default DobVerificationForm;
